import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

/** Regla individual del checklist de cambios de estatus de giro. */
export interface CompanyGiroStatusChangeRuleDto {
  id: string;
  companyId?: string;
  serviceId?: string;
  entryStatusId: string;
  entryStatusLabel: string;
  exitStatusId: string;
  exitStatusLabel: string;
  periodId: string;
  periodLabel: string;
  quantity: number;
}

export interface CompanyGiroStatusChangeRules {
  companyId: string;
  rules: CompanyGiroStatusChangeRuleDto[];
}

export interface CompanyGiroStatusChangeRuleWriteItem {
  entryStatusId: string;
  exitStatusId: string;
  periodId: string;
  quantity: number;
}

export interface CompanyGiroStatusChangeRulesUpsertPayload {
  rules: CompanyGiroStatusChangeRuleWriteItem[];
}

/**
 * Reglas de cambio automático de estatus del servicio giros por empresa.
 *
 * Endpoints:
 *   - GET `/api/structure/companies/{companyId}/giro-status-change-rules`
 *   - PUT `/api/structure/companies/{companyId}/giro-status-change-rules`
 */
@Injectable({ providedIn: 'root' })
export class CompanyGiroStatusChangeRulesService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  get(companyId: string): Observable<CompanyGiroStatusChangeRules> {
    return this.httpClient
      .get<ApiResponse<CompanyGiroStatusChangeRules>>(
        `${this.apiUrl}/${companyId}/giro-status-change-rules`,
      )
      .pipe(map((response) => this.normalize(companyId, response.data)));
  }

  update(
    companyId: string,
    payload: CompanyGiroStatusChangeRulesUpsertPayload,
  ): Observable<CompanyGiroStatusChangeRules> {
    return this.httpClient
      .put<ApiResponse<CompanyGiroStatusChangeRules>>(
        `${this.apiUrl}/${companyId}/giro-status-change-rules`,
        { rules: payload.rules ?? [] },
      )
      .pipe(map((response) => this.normalize(companyId, response.data)));
  }

  private normalize(
    companyId: string,
    data: CompanyGiroStatusChangeRules | null | undefined,
  ): CompanyGiroStatusChangeRules {
    const rules = Array.isArray(data?.rules)
      ? data!.rules.map((r) => ({
          id: String(r.id ?? ''),
          companyId: r.companyId,
          serviceId: r.serviceId,
          entryStatusId: String(r.entryStatusId ?? ''),
          entryStatusLabel: String(r.entryStatusLabel ?? ''),
          exitStatusId: String(r.exitStatusId ?? ''),
          exitStatusLabel: String(r.exitStatusLabel ?? ''),
          periodId: String(r.periodId ?? ''),
          periodLabel: String(r.periodLabel ?? ''),
          quantity: Number(r.quantity) || 0,
        }))
      : [];
    return {
      companyId: data?.companyId || companyId,
      rules,
    };
  }
}
